import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import { useCanWrite } from "../auth/AuthProvider";
import { ConfirmActionModal } from "../components/ConfirmActionModal";
import { OrphanedVolumesCard } from "../components/OrphanedVolumesCard";
import {
  Alert,
  Button,
  buttonStyles,
  Card,
  CardHeader,
  EmptyState,
  Field,
  HealthBadge,
  Input,
  LoadingBlock,
  Mono,
  PageHeader,
  Select,
  ServerStateBadge,
  SuspendedBadge,
  Table,
  Tabs,
  Tag,
  Td,
  Th,
  Textarea,
  TypedPhrase,
  cx,
} from "../components/ui";
import { SATURATED_ABOVE, tightestUsage } from "../lib/capacity";
import { SAFE_ENGINE_VERSION, belowSafeVersion } from "../lib/engineVersion";
import { formatBytes, formatDateTime, formatLatency, formatNumber, formatRelative } from "../lib/format";
import { meshStanding } from "../lib/ha-status";
import { groupLabel } from "../lib/regions";
import {
  useCheckHardening,
  useCheckServer,
  useDeleteServer,
  useIssueFirewallToken,
  useIssueMeshToken,
  useProvisionMonitoring,
  useRecoverServerAction,
  useRegionLookup,
  useRegions,
  useRemoveServerBackupStorage,
  useRunCleanup,
  useRunServerAction,
  useUpdateDokploy,
  useSaveServerBackupStorage,
  useServer,
  useServerBackupStorage,
  useServerDiagnostics,
  useServerDiskUsage,
  useServerMetrics,
  useServerProjects,
  useUpdateServer,
} from "../lib/queries";
import type {
  CleanupAction,
  DokployDiagnosticCheck,
  DokployDiagnosticStatus,
  ServerDiagnostics,
  ServerDetail,
  ServerPool,
  ServerState,
  ServerAction,
} from "../lib/types";

export function ServerDetailPage() {
  const { serverId = "" } = useParams();
  const { data, isPending, error } = useServer(serverId);
  const canWrite = useCanWrite();
  const check = useCheckServer();

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  return (
    <>
      {/* Header and tab strip travel together — the tabs identify the page
          rather than a section of it, so they sit tighter than the 6 of
          `space-y-6` every other block on the page gets. */}
      <div className="space-y-4">
        <PageHeader
          title={data.name}
          description={data.url}
          action={
            <div className="flex flex-wrap items-center gap-2">
              {/* Health rides in the header so it stays legible from every
                  tab: the reason anyone opens Disk is usually that the server
                  looked unwell somewhere else. */}
              <HealthBadge health={data.health.status} />
              <Button
                variant="secondary"
                loading={check.isPending}
                onClick={() => check.mutate(serverId)}
              >
                Run health check
              </Button>
              {canWrite && (
                <Link
                  to={`/servers/${serverId}/settings`}
                  className={buttonStyles("primary", "md")}
                >
                  Settings
                </Link>
              )}
            </div>
          }
        />
        <Tabs
          items={[
            { to: ".", label: "Overview", end: true },
            { to: "diagnostics", label: "Diagnostics" },
            { to: "disk", label: "Disk" },
            { to: "tenants", label: "Tenants" },
            // Nothing on Settings is readable-but-not-writable, so a reader is
            // offered no tab rather than a tab of disabled inputs.
            ...(canWrite ? [{ to: "settings", label: "Settings" }] : []),
          ]}
        />
      </div>

      {check.error && <Alert>{check.error.message}</Alert>}
      {data.health.status === "unauthorized" && (
        <Alert tone="error">
          The engine is rejecting the stored credential. Every tenant on this server is affected
          until it is fixed. Check the endpoint as well as the key: a good key sent to the wrong
          instance fails the same way.
        </Alert>
      )}

      <Outlet context={{ server: data, serverId, canWrite } satisfies ServerTabContext} />

    </>
  );
}

/**
 * The layout fetches the server once and hands it to whichever tab is showing,
 * so switching tabs re-renders rather than refetches. Each tab still owns its
 * own live reads — disk, metrics, projects — which is the point of splitting
 * them up: opening this page no longer asks Dokploy three questions nobody
 * necessarily wanted answered.
 */
interface ServerTabContext {
  server: ServerDetail;
  serverId: string;
  canWrite: boolean;
}

function useServerTab(): ServerTabContext {
  return useOutletContext<ServerTabContext>();
}

export function ServerOverviewTab() {
  const { server, serverId, canWrite } = useServerTab();

  return (
    <>
      {/*
        Figures as tiles rather than a definition list, matching the fleet
        summary on the servers index so the two pages read as one system. A
        hairline grid instead of gaps: on a near-black canvas, space between
        cards reads as more separation than four related numbers deserve.
      */}
      <div className="grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
        <GlanceTile label="Load" note={loadNote(server)}>
          <LoadReading server={server} />
        </GlanceTile>
        <GlanceTile
          label="Last checked"
          note={
            server.health.latencyMs !== null ? formatLatency(server.health.latencyMs) : undefined
          }
        >
          <span className="text-2xl font-light tracking-tight">
            {formatRelative(server.health.checkedAt)}
          </span>
        </GlanceTile>
        <GlanceTile
          label="Organizations"
          note={server.maxOrgs === null ? "no limit" : `of ${server.maxOrgs}`}
        >
          <span className="text-2xl font-light tabular-nums tracking-tight">
            {server.counts?.orgs ?? 0}
          </span>
        </GlanceTile>
        <GlanceTile label="Apps hosted">
          <span className="text-2xl font-light tabular-nums tracking-tight">
            {server.counts?.apps ?? 0}
          </span>
        </GlanceTile>
      </div>

      {server.health.message && (
        <Alert tone={server.health.status === "healthy" ? "info" : "error"}>
          {server.health.message}
        </Alert>
      )}

      <MetricsCard serverId={serverId} monitoringConfigured={server.monitoringConfigured} />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Connection" description="Where we reach the engine, and as whom." />
          <dl className="grid gap-x-6 gap-y-4 px-5 py-4">
            <Detail label="Endpoint">
              {/* Wraps rather than truncates: a half-shown URL is exactly the
                  thing you came here to read character by character. */}
              <Mono className="break-all">{server.url}</Mono>
            </Detail>
            <Detail label="API key">
              <Mono>••••{server.apiKeyHint || "????"}</Mono>
            </Detail>
          </dl>
        </Card>

        <Card>
          <CardHeader title="Placement" description="Whether new tenants land here." />

          {/*
            The verdict first, because it is the question. It used to have to be
            assembled from a state badge, a health badge, a timestamp and a load
            bar spread across two cards — four facts to answer one yes/no.
          */}
          <div className="flex items-start gap-3 border-b border-line px-5 py-4">
            <span
              aria-hidden
              className={cx(
                "mt-1.5 size-2 shrink-0 rounded-full",
                server.placement.eligible ? "bg-mint" : "bg-warn",
              )}
            />
            <div>
              <p className="font-medium text-fg">
                {server.placement.eligible ? "Taking new tenants" : "Not taking new tenants"}
              </p>
              <p className="mt-0.5 text-sm text-fg-subtle">
                {server.placement.reason
                  ? `${server.placement.reason}.`
                  : server.located
                    ? "Eligible, and ranked by distance from the customer."
                    : "Eligible, but with no region it only wins when nothing located can."}
              </p>
            </div>
          </div>

          <dl className="grid gap-x-6 gap-y-4 px-5 py-4 sm:grid-cols-2">
            <Detail label="Placement state">
              <ServerStateBadge state={server.state} />
            </Detail>
            <Detail label="Pool">
              {server.pool === "dedicated" ? (
                <>
                  <Tag tone="accent">Dedicated</Tag>
                  <p className="mt-1 text-xs text-fg-subtle">
                    Automatic placement never chooses this server. Only pinned tenants land here.
                  </p>
                </>
              ) : (
                "Shared"
              )}
            </Detail>
            <Detail label="Region">
              {server.located ? (
                <RegionName code={server.region} />
              ) : (
                <span className="text-warn">Not set — skipped by distance</span>
              )}
            </Detail>
            <Detail label="Position">
              {server.coordinates ? (
                <>
                  <Mono>
                    {server.coordinates.latitude.toFixed(2)},{" "}
                    {server.coordinates.longitude.toFixed(2)}
                  </Mono>
                  <p className="text-xs text-fg-subtle">Overrides the region.</p>
                </>
              ) : server.region ? (
                <span className="text-fg-subtle">From the region</span>
              ) : (
                "—"
              )}
            </Detail>
            <Detail label="Outbound address">
              <EgressValue egress={server.egress} />
            </Detail>
            <Detail label="Registered">{formatDateTime(server.createdAt)}</Detail>
          </dl>
        </Card>
      </div>

      <EngineVersionCard server={server} canWrite={canWrite} />

      <div className="grid gap-6 lg:grid-cols-2">
        <FirewallCard server={server} canWrite={canWrite} />
        <MeshCard server={server} canWrite={canWrite} />
      </div>

      <HardeningCard server={server} canWrite={canWrite} />

      {server.notes && (
        <Card>
          <CardHeader title="Notes" />
          <div className="px-5 py-4 text-sm whitespace-pre-wrap">{server.notes}</div>
        </Card>
      )}
    </>
  );
}

/**
 * What tenants on this machine appear to call out from, and where the figure
 * came from.
 *
 * Customers paste this into their own firewall to let a workload here reach a
 * database inside their VPC, so the two failure shapes both have to be visible
 * rather than smoothed over. An unknown address says so instead of rendering
 * blank — a blank copied into a firewall rule is a rule matching nothing. And a
 * stated value the agent contradicts is flagged rather than quietly preferred:
 * whichever is wrong, the customer's connection fails on their infrastructure,
 * where nothing here can see it.
 */
function EgressValue({ egress }: { egress: ServerDetail["egress"] }) {
  if (egress.source === "none") {
    return (
      <>
        <span className="text-fg-subtle">Unknown</span>
        <p className="mt-1 text-xs text-fg-subtle">
          No agent has polled and no address is set. Customers in this region cannot be given one.
        </p>
      </>
    );
  }

  return (
    <>
      <Mono>{egress.address}</Mono>
      <p className="mt-1 text-xs text-fg-subtle">
        {egress.source === "stated" ? "Set by an operator." : "Observed from the agent's poll."}
      </p>
      {egress.disagrees && (
        <p className="mt-1 text-xs text-warn">
          The agent polls from a different address. One of the two is being published to customers
          wrongly.
        </p>
      )}
    </>
  );
}

/**
 * What the engine is running, and what an update is doing about it.
 *
 * On the overview rather than behind the Diagnostics tab's live snapshot,
 * because of when it is needed: an update restarts the engine, so for the
 * minutes that matter most a live check cannot answer and its failure would
 * hide the update entirely. Everything here comes from the control plane's own
 * tables — the version the sweep last sampled, and the attempt row.
 *
 * The card is a status, never an action. Starting an update needs a live read
 * of what the engine would install, and that belongs with the other live reads.
 * The one button here is the acknowledgement, which asks the engine nothing and
 * is the thing an operator comes to this page to do after an update stalled.
 */
export function EngineVersionCard({ server, canWrite }: { server: ServerDetail; canWrite: boolean }) {
  const attempt = server.update.attempt;
  const running = attempt?.outcome === "pending";
  const needsAck = attempt?.outcome === "unknown";
  /*
   * An attempt that has not been settled — still installing, or stalled and
   * waiting for a person. Both mean this server is drained and no second
   * update may be asked for, so both have to suppress the offer of one. Only
   * `pending` was checked at first, and a stalled server went on inviting an
   * update it would refuse and showing "an update is waiting" over a red alert
   * saying it had failed.
   */
  const inFlight = running || needsAck;
  const stalled = needsAck || (running && attempt.overdue);

  return (
    <Card className={stalled ? "border-danger/30" : undefined}>
      <CardHeader
        title="Engine version"
        description="What this machine runs, and what an update is doing."
        action={
          server.updateAvailable === true && !inFlight ? (
            <Link
              to={`/servers/${server.id}/diagnostics`}
              className="text-sm text-accent hover:underline"
            >
              Update &rarr;
            </Link>
          ) : undefined
        }
      />
      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-2xl font-light tracking-tight text-fg">
            {server.version ?? "Not reported"}
          </span>
          {/*
            The attempted version stays on screen after a stall, dimmed to
            danger rather than dropped: "what did it try to install" is the
            first thing asked about a server that is stuck, and replacing it
            with the ordinary "update available" tag threw that away.
          */}
          {inFlight && attempt.targetVersion && (
            <>
              <span className="text-lg text-fg-subtle">&rarr;</span>
              <span
                className={cx(
                  "font-mono text-2xl font-light tracking-tight",
                  needsAck ? "text-danger" : "text-warn",
                )}
              >
                {attempt.targetVersion}
              </span>
            </>
          )}
          {!inFlight && server.updateAvailable === true && server.latestVersion && (
            <Tag tone="warn">{server.latestVersion} available</Tag>
          )}
          {belowSafeVersion(server.version) === true && (
            <Tag tone="bad">Unsafe below {SAFE_ENGINE_VERSION}</Tag>
          )}
        </div>
        {belowSafeVersion(server.version) === true && (
          <Alert tone="error">
            <strong>This engine runs customer input unquoted.</strong> Before {SAFE_ENGINE_VERSION} Dokploy interpolated branch names, repository URLs and
            scheduled commands straight into the shell it runs as root on this server. The control
            plane refuses the characters that break out, but the engine quoting what it runs is
            the boundary that matters. Update this server before it takes another tenant.
          </Alert>
        )}

        {/*
          Three states, and the third is the one worth spelling out. Null is
          Dokploy not having answered, which is not the same as up to date —
          rendering it as "current" would report an unpatched machine as fine.
        */}
        <p className="text-[13px] text-fg-muted">
          {needsAck ? (
            <>
              Started {formatRelative(attempt.requestedAt)} and never reported the new version.
              This server is drained and no further update can be requested until somebody has
              looked at the machine.
            </>
          ) : running ? (
            <>
              Updating since {formatRelative(attempt.requestedAt)}. The engine cannot report its
              own restart, so Maxlayer is watching for{" "}
              <Mono>{attempt.targetVersion ?? "the new version"}</Mono> and returns this server to
              service when it appears. This page is refreshing itself.
            </>
          ) : server.updateAvailable === null ? (
            <>
              This engine has not reported whether an update is available. That is not the same as
              being current — older releases have no such endpoint.
            </>
          ) : server.updateAvailable === true ? (
            <>An update is waiting. Run diagnostics on this server to install it.</>
          ) : (
            <>Up to date as of the last check.</>
          )}
        </p>

        {/*
          Only where the alert adds a consequence the line above did not. A
          stalled attempt already explains itself; what it needs is the button.
        */}
        {stalled && !needsAck && (
          <Alert tone="error">
            This update is taking longer than expected. If the version does not appear, the server
            stays draining until somebody acknowledges it.
          </Alert>
        )}

        {canWrite && attempt?.outcome === "unknown" && (
          <Link
            to={`/servers/${server.id}/diagnostics`}
            className={buttonStyles("secondary", "sm")}
          >
            Check and return to service
          </Link>
        )}

        <p className="text-xs text-fg-subtle">
          Sampled {formatRelative(server.versionCheckedAt)}
          {server.state === "draining" && inFlight ? " · draining for this update" : ""}
        </p>
      </div>
    </Card>
  );
}

const FIREWALL_STANDING = {
  applied: { label: "Applied", dot: "text-mint", body: "Allowlists are in force." },
  pending: { label: "Pending", dot: "text-warn", body: "Waiting for the agent to confirm the latest rules." },
  unreachable: {
    label: "Unreachable",
    dot: "text-danger",
    body: "No poll for 5 minutes. The last rules still stand on the box; changes wait.",
  },
  unprovisioned: {
    label: "Not installed",
    dot: "text-fg-subtle",
    body: "Tenant allowlists here are recorded, not enforced — and their dashboards say so.",
  },
} as const;

/*
 * Container hardening, as the engine actually applies it.
 *
 * The card shows the sweep's summary -- how many containers were running,
 * how many the control plane placed, how many are foreign, and findings by
 * severity -- and then every container that has anything above info, with
 * its findings. A container with only info findings (root inside the
 * container, a writable root filesystem, which is every official database
 * image) is counted and not listed, because a list of twenty identical
 * "runs as root" rows is how the one privileged container gets missed.
 *
 * Nothing here changes a container. The button re-reads; the sweep does the
 * same every six hours. What a finding means and what to do about it is in
 * OPERATIONS under "Container hardening".
 */
const SEVERITY_TONE = { critical: "bad", warning: "warn", info: "neutral" } as const;
const KIND_LABEL = { workload: "workload", platform: "platform", foreign: "not ours" } as const;

export function HardeningCard({ server, canWrite }: { server: ServerDetail; canWrite: boolean }) {
  const check = useCheckHardening(server.id);
  const posture = server.hardening;
  const listed = posture?.containers.filter((c) => c.findings.some((f) => f.severity !== "info")) ?? [];
  const tone = !posture ? "neutral" : posture.critical > 0 ? "bad" : posture.warning > 0 ? "warn" : "good";
  const headline = !posture
    ? "Not read yet"
    : posture.critical > 0
      ? `${posture.critical} critical`
      : posture.warning > 0
        ? `${posture.warning} warning${posture.warning === 1 ? "" : "s"}`
        : "Contained";

  return (
    <Card>
      <CardHeader
        title="Container hardening"
        description="What the engine applies to every running container, read through Dokploy."
        action={
          canWrite ? (
            <Button variant="secondary" size="sm" loading={check.isPending} onClick={() => check.mutate()}>
              {posture ? "Read again" : "Read now"}
            </Button>
          ) : undefined
        }
      />
      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Tag tone={tone}>{headline}</Tag>
          {posture && (
            <span className="text-xs text-fg-subtle">
              {posture.inspected} of {posture.running} running inspected · {posture.workloads} placed here ·{" "}
              {posture.foreign} not ours · {posture.info} at info · read {formatRelative(posture.checkedAt)}
              {posture.failed > 0 ? ` · ${posture.failed} could not be read` : ""}
            </span>
          )}
        </div>

        {check.error && <Alert>{check.error.message}</Alert>}
        {posture?.error && (
          <Alert tone="error">
            The last read could not reach the engine ({posture.error}); the containers below are from the
            read before it.
          </Alert>
        )}

        {!posture && (
          <p className="text-[13px] text-fg-muted">
            The sweep reaches every healthy server within six hours. Reading now asks the engine for
            each running container's configuration and judges it against what the platform promises:
            no privilege, no Docker socket, no host namespaces, the default seccomp and AppArmor
            profiles, and the plan's memory and CPU as real limits.
          </p>
        )}

        {posture && listed.length === 0 && (
          <p className="text-[13px] text-fg-muted">
            Every container is contained. What remains is at info — root inside the container and a
            writable root filesystem, which is how every official database image runs — and is
            counted above rather than listed.
          </p>
        )}

        {listed.length > 0 && (
          <ul className="divide-y divide-line rounded-md border border-line">
            {listed.map((container) => (
              <li key={container.id} className="space-y-2 px-3 py-3">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Mono className="text-sm">{container.service ?? container.name}</Mono>
                  <Tag tone={container.kind === "foreign" ? "warn" : "neutral"}>{KIND_LABEL[container.kind]}</Tag>
                  {container.image && <span className="text-xs text-fg-subtle break-all">{container.image}</span>}
                  {/*
                    Which networks the daemon has it on. A sealed workload's
                    finding says it is on the shared overlay; this says what
                    else it is on, which is how "attached but not yet
                    restarted" reads apart from "never attached".
                  */}
                  {(container.networks ?? []).length > 0 && (
                    <span className="text-xs text-fg-subtle">on {container.networks.join(", ")}</span>
                  )}
                  {/*
                    Who it belongs to, and a way there. A finding used to name
                    a container and stop -- which is a string an operator has
                    to resolve into a tenant before they can do anything about
                    it, and the mapping was one join away the whole time.
                  */}
                  {container.owner && (
                    <span className="text-xs text-fg-subtle">
                      <Link
                        to={container.owner.kind === "app" ? `/apps/${container.owner.id}` : `/organizations/${container.owner.orgId}`}
                        className="text-accent hover:underline"
                      >
                        {container.owner.name}
                      </Link>{" "}
                      · {container.owner.orgName}
                    </span>
                  )}
                </div>
                <ul className="space-y-1">
                  {container.findings
                    .filter((f) => f.severity !== "info")
                    .map((finding, index) => (
                      <li key={`${finding.code}-${index}`} className="flex items-start gap-2 text-[13px]">
                        <Tag tone={SEVERITY_TONE[finding.severity]}>{finding.severity}</Tag>
                        <span className="text-fg-muted">{finding.detail}</span>
                      </li>
                    ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

function FirewallCard({ server, canWrite }: { server: ServerDetail; canWrite: boolean }) {
  const issue = useIssueFirewallToken(server.id);
  const standing = FIREWALL_STANDING[server.firewallStanding];
  const [copied, setCopied] = useState(false);

  /* A shown-once credential with no copy button is a transcription exercise. */
  async function copyToken() {
    if (!issue.data) return;
    try {
      await navigator.clipboard.writeText(issue.data.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* select-all on the code block remains the fallback */
    }
  }

  return (
    <Card>
      <CardHeader
        title="Firewall agent"
        description="Enforces database access allowlists on this machine."
      />
      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-2 text-sm font-medium text-fg">
            <span className={`dot-status ${standing.dot}`} aria-hidden="true" />
            {standing.label}
          </span>
          {server.firewall.seenAt && (
            <span className="text-xs text-fg-subtle">
              polled {formatRelative(server.firewall.seenAt)}
            </span>
          )}
          {server.firewall.provisioned && (
            <span className="font-mono text-xs text-fg-subtle">
              token &hellip;{server.firewall.tokenHint}
            </span>
          )}
        </div>
        <p className="text-[13px] text-fg-muted">{standing.body}</p>

        {issue.data ? (
          <div className="space-y-2">
            <p className="text-[13px] text-warn">
              Shown once. Deploy per <span className="font-mono">agent/firewall/README.md</span> as
              FIREWALL_AGENT_TOKEN.
            </p>
            <div className="flex items-stretch gap-2">
              <code className="ident block min-w-0 flex-1 rounded-md border border-line bg-canvas px-3 py-2 text-fg select-all">
                {issue.data.token}
              </code>
              <Button variant="secondary" size="sm" onClick={() => void copyToken()}>
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        ) : (
          canWrite && (
            <div className="space-y-2">
              <Button
                variant="secondary"
                size="sm"
                loading={issue.isPending}
                onClick={() => void issue.mutateAsync()}
              >
                {server.firewall.provisioned ? "Rotate token" : "Set up agent"}
              </Button>
              {server.firewall.provisioned && (
                <p className="text-xs text-fg-subtle">
                  Rotating orphans the running agent until its env var is updated.
                </p>
              )}
            </div>
          )
        )}
      </div>
    </Card>
  );
}

const MESH_STANDING = {
  applied: { label: "Applied", dot: "text-mint", body: "The mesh agent has checked in." },
  pending: {
    label: "Pending",
    dot: "text-warn",
    body: "The agent is checking in, and a change is still waiting.",
  },
  unreachable: {
    label: "Unreachable",
    dot: "text-danger",
    body: "No check-in for 5 minutes. The last mesh state still stands; changes wait.",
  },
  not_enrolled: {
    label: "Not enrolled",
    dot: "text-fg-subtle",
    body: "No mesh agent has checked in. High availability cannot use this machine yet.",
  },
} as const;

export function MeshCard({ server, canWrite }: { server: ServerDetail; canWrite: boolean }) {
  const issue = useIssueMeshToken(server.id);
  const standingKey = meshStanding(server);
  const standing = MESH_STANDING[standingKey];
  const [copied, setCopied] = useState(false);
  const seenAt = typeof server.meshSeenAt === "string" ? server.meshSeenAt : null;
  const nodeCount =
    typeof server.haNodeCount === "number" && Number.isFinite(server.haNodeCount)
      ? server.haNodeCount
      : null;

  async function copyToken() {
    if (!issue.data?.token) return;
    try {
      await navigator.clipboard.writeText(issue.data.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* select-all on the code block remains the fallback */
    }
  }

  return (
    <Card className="min-w-0">
      <CardHeader
        title="Mesh agent"
        description="The host mesh this machine uses for database high availability."
      />
      <div className="space-y-4 px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-2 text-sm font-medium text-fg">
            <span className={`dot-status ${standing.dot}`} aria-hidden="true" />
            {standing.label}
          </span>
          {standingKey !== "not_enrolled" && seenAt && (
            <span className="text-xs text-fg-subtle">checked in {formatRelative(seenAt)}</span>
          )}
        </div>
        <p className="text-[13px] leading-5 text-fg-muted">{standing.body}</p>
        {/*
          Site and how many nodes, nothing else. The mesh address and its port
          are not an operator control, and a card that prints them invites
          someone to dial the overlay from a laptop.
        */}
        {(server.site || nodeCount !== null) && (
          <p className="text-xs text-fg-subtle">
            {server.site}
            {server.site && nodeCount !== null ? " · " : ""}
            {nodeCount !== null && (
              <>
                {formatNumber(nodeCount)} {nodeCount === 1 ? "HA node" : "HA nodes"}
              </>
            )}
          </p>
        )}

        {issue.error && <Alert>{issue.error.message}</Alert>}

        {issue.data?.token ? (
          <div className="space-y-2">
            <p className="text-[13px] text-warn">Shown once. It is the mesh agent's credential for this machine.</p>
            <div className="flex items-stretch gap-2">
              <code className="ident block min-w-0 flex-1 rounded-md border border-line bg-canvas px-3 py-2 text-fg select-all">
                {issue.data.token}
              </code>
              <Button variant="secondary" size="sm" onClick={() => void copyToken()}>
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        ) : (
          canWrite && (
            <Button
              variant="secondary"
              size="sm"
              loading={issue.isPending}
              onClick={() => void issue.mutateAsync()}
            >
              {standingKey === "not_enrolled" ? "Issue token" : "Rotate token"}
            </Button>
          )
        )}
      </div>
    </Card>
  );
}

export function ServerDiskTab() {
  const { server, serverId, canWrite } = useServerTab();
  const metrics = useServerMetrics(serverId);
  const disk = useServerDiskUsage(serverId);

  return (
    <>
      <MachineDiskCard metrics={metrics} disk={disk} />
      <DockerBreakdownCard disk={disk} />
      {/* Before cleanup: it is the precise way to take volumes back, and the
          blunt one below would take a stopped database's with them. */}
      <OrphanedVolumesCard serverId={serverId} serverName={server.name} canWrite={canWrite} />
      <CleanupCard serverId={serverId} canWrite={canWrite} />
    </>
  );
}

export function ServerDiagnosticsTab() {
  const { server, serverId, canWrite } = useServerTab();
  const [requested, setRequested] = useState(false);
  const [confirmingRestart, setConfirmingRestart] = useState(false);
  // Which action's unresolved attempt is being acknowledged, or null. One
  // modal for both, because the acknowledgement is the same act — an operator
  // saying they have looked at the machine — and only the copy differs.
  const [confirmingRecovery, setConfirmingRecovery] = useState<ServerAction | null>(null);
  const [confirmingUpdate, setConfirmingUpdate] = useState(false);
  const [restartRequested, setRestartRequested] = useState(false);
  const [updateDispatched, setUpdateDispatched] = useState(false);
  const diagnostics = useServerDiagnostics(serverId);
  const restart = useRunServerAction(serverId);
  const update = useUpdateDokploy(serverId);
  const recover = useRecoverServerAction(serverId);
  const restartState = diagnostics.data?.actions.restartTraefik;
  const selfHosted = diagnostics.data?.cloud.available && diagnostics.data.cloud.value === false;

  /*
   * Whether an update may be asked for. The control plane checks all of this
   * again and is the boundary; this decides whether to offer a button, and
   * says why when it does not.
   *
   * `updateAvailable` must be a definite true. Null is Dokploy not having
   * said — an older release with no such endpoint — and offering an update on
   * that basis would drain a server to install nothing.
   */
  /*
   * The attempt and the fleet lock come from the server record — rows, not the
   * engine — so they answer even while the engine is restarting. Only "is
   * there an update, and which version" needs the live snapshot, because only
   * the engine knows it.
   */
  const attempt = server.update.attempt;
  const blockedBy = server.update.blockedBy;
  const updateRunning = attempt?.outcome === "pending";
  const needsAck = attempt?.outcome === "unknown";

  const updateReady =
    requested &&
    diagnostics.isSuccess &&
    !diagnostics.error &&
    selfHosted &&
    diagnostics.data?.update.updateAvailable === true &&
    Boolean(diagnostics.data?.update.latestVersion) &&
    !updateRunning &&
    !needsAck &&
    !blockedBy &&
    !diagnostics.isFetching &&
    !diagnostics.isStale &&
    !updateDispatched;

  /* Ordered by what an operator can do something about, soonest first. */
  let updateBlocker: string | null = null;
  if (needsAck) {
    updateBlocker =
      "The last update never reported the version it should have installed, and this server is still draining. Check the machine, then return it to service below.";
  } else if (updateRunning || updateDispatched) {
    updateBlocker = "An update is running on this server.";
  } else if (blockedBy) {
    updateBlocker = `${blockedBy.name} is updating. Maxlayer updates one server at a time, so a bad release cannot take the fleet down at once.`;
  } else if (!requested) {
    updateBlocker = "Run diagnostics to read the installed and available versions.";
  } else if (diagnostics.isFetching) {
    updateBlocker = "Waiting for the version check.";
  } else if (diagnostics.error) {
    updateBlocker = "Diagnostics did not complete, so the update is unavailable.";
  } else if (diagnostics.isStale) {
    updateBlocker = "Refresh diagnostics to load the current update state.";
  } else if (!diagnostics.data?.cloud.available || diagnostics.data.cloud.value === null) {
    updateBlocker = "The update is disabled until this endpoint is confirmed self-hosted.";
  } else if (diagnostics.data.cloud.value) {
    updateBlocker = "Dokploy Cloud instances update themselves.";
  } else if (diagnostics.data?.update.available === false) {
    updateBlocker =
      diagnostics.data.update.reason ??
      "This Dokploy does not report update information, so Maxlayer could not confirm an update afterwards.";
  } else if (diagnostics.data?.update.updateAvailable === false) {
    updateBlocker = "Dokploy reports this server is already up to date.";
  } else if (diagnostics.data?.update.updateAvailable === null) {
    updateBlocker = "Dokploy did not report whether an update is available.";
  } else if (!diagnostics.data?.update.latestVersion) {
    updateBlocker =
      "Dokploy reports an update but not its version, so the result could not be confirmed.";
  }

  const restartReady =
    requested &&
    diagnostics.isSuccess &&
    !diagnostics.error &&
    selfHosted &&
    diagnostics.data?.infrastructure.available === true &&
    restartState?.outcome !== "pending" &&
    !restartState?.requiresRecovery &&
    restartState?.canRequestRestart !== false &&
    !diagnostics.isFetching &&
    !diagnostics.isStale &&
    !restartRequested;
  let restartBlocker: string | null = null;
  if (restartRequested) {
    restartBlocker = "A restart request was accepted. Refresh diagnostics after recovery before requesting another.";
  } else if (!requested) {
    restartBlocker = "Run diagnostics first to verify the stored key has administrator access.";
  } else if (diagnostics.isFetching) {
    restartBlocker = "Waiting for the administrator-capability check.";
  } else if (diagnostics.error) {
    restartBlocker = "Diagnostics did not complete, so Traefik restart is unavailable.";
  } else if (diagnostics.isStale) {
    restartBlocker = "Refresh diagnostics to load the latest Traefik restart state.";
  } else if (!diagnostics.data?.cloud.available || diagnostics.data.cloud.value === null) {
    restartBlocker = "Traefik restart is disabled until this endpoint is confirmed self-hosted.";
  } else if (diagnostics.data.cloud.value) {
    restartBlocker = "Traefik restart is unavailable for hosted cloud or an unscoped remote node.";
  } else if (restartState?.requiresRecovery) {
    restartBlocker = "The prior Traefik restart has an unresolved outcome. Verify ingress manually, then acknowledge recovery before requesting another restart.";
  } else if (restartState?.outcome === "pending") {
    restartBlocker = "A Traefik restart is still being dispatched. Refresh diagnostics after the engine reports its result.";
  } else if (restartState && !restartState.canRequestRestart) {
    restartBlocker = "A Traefik restart was recently accepted. Wait one minute, then refresh diagnostics before requesting another.";
  } else if (diagnostics.data?.infrastructure.available === false) {
    restartBlocker =
      diagnostics.data.infrastructure.reason ??
      "The stored key cannot run administrator-only Settings actions.";
  }

  function loadDiagnostics() {
    setRestartRequested(false);
    setUpdateDispatched(false);
    setRequested(true);
    void diagnostics.refetch();
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Engine diagnostics"
          description="A live, non-sensitive Settings snapshot. It is never loaded with the server record."
          action={
            <Button
              variant="secondary"
              size="sm"
              loading={requested && diagnostics.isFetching}
              onClick={loadDiagnostics}
            >
              {requested ? "Refresh" : "Run diagnostics"}
            </Button>
          }
        />
        <div className="p-5">
          {!requested ? (
            <EmptyState
              title="Run a live diagnostic snapshot"
              description="Checks platform version, release channel, update data, database health, and ingress health."
            />
          ) : diagnostics.isPending ? (
            <LoadingBlock label="Asking the engine..." />
          ) : diagnostics.error ? (
            <Alert>{diagnostics.error.message}</Alert>
          ) : diagnostics.data ? (
            <DiagnosticsSnapshot data={diagnostics.data} />
          ) : null}
        </div>
      </Card>

      {canWrite && (
        <Card className="border-danger/30">
          <CardHeader
            title="Emergency ingress restart"
            description="Use only when incident evidence points to Traefik on this engine."
          />
          <div className="space-y-4 px-5 py-4">
            {restartBlocker && <Alert tone="info">{restartBlocker}</Alert>}
            <p className="text-sm leading-6 text-fg-muted">
              The engine answers before the restart finishes. Routing can drop while Traefik
              restarts, so check the affected sites and run diagnostics again.
            </p>
            <Button
              variant="danger"
              disabled={!restartReady}
              onClick={() => setConfirmingRestart(true)}
            >
              Restart Traefik
            </Button>
            {restartState?.requiresRecovery && (
              <Button
                variant="secondary"
                disabled={!restartState.attemptId}
                onClick={() => setConfirmingRecovery("restart-traefik")}
              >
                Acknowledge recovery
              </Button>
            )}
          </div>
        </Card>
      )}

      {canWrite && (
        <Card className={needsAck ? "border-danger/30" : "border-warn/30"}>
          <CardHeader
            title="Update Dokploy"
            description="Installs the release this engine reports as available."
          />
          <div className="space-y-4 px-5 py-4">
            {/*
              The version transition first and largest, because it is the whole
              decision. Everything under it is what happens around that change.
            */}
            {updateReady && (
              <p className="flex flex-wrap items-baseline gap-x-2 font-mono text-xl font-light tracking-tight">
                <span className="text-fg">{diagnostics.data?.version.value ?? "unknown"}</span>
                <span className="text-fg-subtle">&rarr;</span>
                <span className="text-warn">{diagnostics.data?.update.latestVersion}</span>
              </p>
            )}

            {updateBlocker && <Alert tone={needsAck ? "error" : "info"}>{updateBlocker}</Alert>}

            {/*
              What the platform does around the call, as facts rather than a
              paragraph. This is read once by each operator and skipped every
              time after, so it has to survive being skimmed — the second line
              is the one that is genuinely irreversible.
            */}
            {!updateRunning && !needsAck && (
              <dl className="grid gap-x-4 gap-y-2 text-[13px] sm:grid-cols-[9rem_1fr]">
                <dt className="text-fg-subtle">Tenants here</dt>
                <dd className="text-fg-muted">
                  Keep serving traffic. Nothing deploys until the engine is back.
                </dd>
                <dt className="text-fg-subtle">Rollback</dt>
                <dd className="text-fg-muted">
                  None. Dokploy&rsquo;s update is a one-way image pull.
                </dd>
                <dt className="text-fg-subtle">Placement</dt>
                <dd className="text-fg-muted">
                  Drained for the duration, then restored. Empty placements move away and stay
                  away; pinned tenants do not move.
                </dd>
                <dt className="text-fg-subtle">Fleet</dt>
                <dd className="text-fg-muted">One server at a time.</dd>
                <dt className="text-fg-subtle">Confirmation</dt>
                <dd className="text-fg-muted">
                  By watching the reported version — the engine restarts the request, so it cannot
                  answer for itself.
                </dd>
              </dl>
            )}

            <div className="flex flex-wrap gap-3">
              <Button
                variant="danger"
                disabled={!updateReady}
                onClick={() => setConfirmingUpdate(true)}
              >
                Update Dokploy
              </Button>
              {needsAck && attempt && (
                <Button variant="secondary" onClick={() => setConfirmingRecovery("update-dokploy")}>
                  Check and return to service
                </Button>
              )}
            </div>

            {/*
              The SLA excludes planned maintenance only where it was announced
              24 hours ahead. Said here rather than only in the runbook: this is
              the screen where somebody decides to do it.
            */}
            {updateReady && (
              <p className="text-xs text-fg-subtle">
                Announce this 24 hours ahead unless it is a security release — the SLA only
                excludes planned maintenance that was.
              </p>
            )}
          </div>
        </Card>
      )}

      <ConfirmActionModal
        open={confirmingUpdate}
        onClose={() => {
          setConfirmingUpdate(false);
          update.reset();
        }}
        onConfirm={() =>
          update.mutate(undefined, {
            onSuccess: () => {
              setUpdateDispatched(true);
              update.reset();
              setConfirmingUpdate(false);
            },
          })
        }
        title={`Update Dokploy on ${server.name}?`}
        description={
          diagnostics.data?.update.latestVersion
            ? `This installs ${diagnostics.data.update.latestVersion} and restarts the engine.`
            : "This installs the available release and restarts the engine."
        }
        consequence="Every tenant on this server is affected while the engine restarts, and this cannot be undone. The server is drained first and returns to service once Maxlayer sees the new version; if it never appears, the server stays drained for you to check."
        confirmLabel="Update Dokploy"
        danger
        loading={update.isPending}
        error={update.error}
      />

      <ConfirmActionModal
        open={confirmingRestart}
        onClose={() => {
          setConfirmingRestart(false);
          restart.reset();
        }}
        onConfirm={() =>
          restart.mutate("restart-traefik", {
            onSuccess: () => {
              setRestartRequested(true);
              restart.reset();
              setConfirmingRestart(false);
            },
          })
        }
            title={`Restart Traefik on ${server.name}?`}
            description="This restarts the ingress service for the registered engine."
            consequence="Traffic can be interrupted while the engine recreates Traefik. The request is audited, but the engine completes it in the background and cannot report success here."
        confirmLabel="Restart Traefik"
        danger
        loading={restart.isPending}
        error={restart.error}
      />

      <ConfirmActionModal
        open={confirmingRecovery !== null}
        onClose={() => {
          setConfirmingRecovery(null);
          recover.reset();
        }}
        onConfirm={() => {
          const action = confirmingRecovery;
          const attemptId =
            /*
             * The update's attempt id comes from the server record, not from
             * the live snapshot. A stalled update usually means the engine is
             * not answering, so `diagnostics` is an error and `updateState` is
             * undefined — and this button, the one that returns the server to
             * service, would silently do nothing at exactly the moment it is
             * needed. The restart has no such problem: its recovery is about
             * ingress, which does not stop the Settings API answering.
             */
            action === "update-dokploy" ? attempt?.attemptId : restartState?.attemptId;
          if (!action || !attemptId) return;
          recover.mutate(
            { action, attemptId },
            {
              onSuccess: () => {
                recover.reset();
                setConfirmingRecovery(null);
              },
            },
          );
        }}
        title={
          confirmingRecovery === "update-dokploy"
            ? `Return ${server.name} to service?`
            : `Acknowledge recovery on ${server.name}?`
        }
        description={
          confirmingRecovery === "update-dokploy"
            ? "This clears an update whose result Maxlayer never saw, and undrains the server."
            : "This unlocks a restart request whose outcome Maxlayer could not confirm."
        }
        consequence={
          confirmingRecovery === "update-dokploy"
            ? "This does not update anything and does not check the engine. Confirm the machine is healthy and running the version you expect before returning it to service — new tenants will be placed here again. The acknowledgement is audited."
            : "This does not restart Traefik. Confirm that ingress has been checked manually before allowing another restart request. The acknowledgement is audited."
        }
        confirmLabel={
          confirmingRecovery === "update-dokploy"
            ? "Return to service"
            : "Acknowledge recovery"
        }
        danger
        loading={recover.isPending}
        error={recover.error}
      />
    </>
  );
}

export function ServerTenantsTab() {
  const { server, serverId } = useServerTab();
  const [showProjects, setShowProjects] = useState(false);

  return (
    <>
      <Card>
        <CardHeader
          title="Tenants on this server"
          description={`${server.organizations.length} organization(s)`}
        />
        {server.organizations.length === 0 ? (
          <EmptyState
            title="No tenants placed here yet"
            description="New placements land on the least-loaded active server in their region."
          />
        ) : (
          <Table
            head={
              <tr>
                <Th>Organization</Th>
                <Th>Region</Th>
                <Th numeric>Apps</Th>
                <Th numeric>Databases</Th>
                <Th>Status</Th>
              </tr>
            }
          >
            {server.organizations.map((org) => (
              // One row per placement: the same organization can appear on
              // several servers, once per region it deploys in.
              <tr key={`${org.id}-${org.region ?? "none"}`} className="hover:bg-raised">
                <Td>
                  <Link
                    to={`/organizations/${org.id}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {org.name}
                  </Link>
                  <p className="text-xs text-fg-subtle">{org.slug}</p>
                </Td>
                <Td>{groupLabel(org.region) ?? <span className="text-fg-subtle">—</span>}</Td>
                <Td numeric>{org.apps}</Td>
                <Td numeric>{org.databases}</Td>
                <Td>{org.suspendedAt ? <SuspendedBadge /> : "Active"}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Projects"
          description="Read live, to reconcile against our records."
          action={
            <Button variant="secondary" size="sm" onClick={() => setShowProjects((v) => !v)}>
              {showProjects ? "Hide" : "Load projects"}
            </Button>
          }
        />
        {showProjects && <ProjectList serverId={serverId} />}
      </Card>
    </>
  );
}

export function ServerSettingsTab() {
  const { server, serverId, canWrite } = useServerTab();

  // The tab is not offered to readers, but a pasted URL still lands here.
  if (!canWrite) {
    return <Alert tone="info">Changing server settings needs superadmin access.</Alert>;
  }

  /*
   * Two columns, with what the server *is* pinned beside what is being changed
   * about it. The forms here are long — name, placement, coordinates, capacity,
   * notes, plus a connection and an object store — and every one of them is
   * edited while checking a fact that used to be a tab away: is this thing
   * healthy, is it taking tenants, how many does it already hold.
   *
   * The danger zone sits below both columns rather than as a fourth peer card in
   * the stack, so deleting a server is not one scroll-stop away from renaming it.
   */
  return (
    <div className="space-y-6">
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-6">
          {/* Keyed on the URL so a saved endpoint change re-seeds the field rather
              than leaving the old address in it. */}
          <ConnectionCard key={server.url} serverId={serverId} currentUrl={server.url} />
          <SettingsCard serverId={serverId} initial={server} />
          <BackupStorageCard serverId={serverId} />
        </div>

        <aside className="lg:sticky lg:top-6">
          <ServerFacts server={server} />
        </aside>
      </div>

      <DangerZone serverId={serverId} name={server.name} />
    </div>
  );
}

/**
 * What this server is, while you change it.
 *
 * Placement eligibility leads because it is the question these forms exist to
 * answer — a name or a note changes nothing about whether tenants land here, and
 * the region, the state and the pool all do. The API computes that verdict with
 * the same function placement itself runs, so this states the real rule rather
 * than a copy of it that can fall behind.
 */
function ServerFacts({ server }: { server: ServerDetail }) {
  return (
    <Card>
      <CardHeader title="This server" />

      <div className="flex items-start gap-3 border-b border-line px-5 py-4">
        <span
          aria-hidden
          className={cx(
            "mt-1.5 size-2 shrink-0 rounded-full",
            server.placement.eligible ? "bg-mint" : "bg-warn",
          )}
        />
        <p className="text-xs leading-5 text-fg-muted">
          {server.placement.eligible
            ? "Taking new tenants."
            : (server.placement.reason ?? "Not taking new tenants.")}
        </p>
      </div>

      <dl className="divide-y divide-line text-xs">
        {(
          [
            ["Health", server.health.status],
            ["Endpoint", server.url],
            ["API key", `••••${server.apiKeyHint || "????"}`],
            ["Region", server.region ?? "Not set"],
            [
              "Tenants",
              server.counts
                ? `${server.counts.orgs} orgs · ${server.counts.apps} apps`
                : "Not counted",
            ],
            ["Engine", server.version ?? "Unknown"],
          ] as [string, string][]
        ).map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-3 px-5 py-2.5">
            <dt className="shrink-0 text-fg-subtle">{label}</dt>
            <dd className="ident min-w-0 truncate text-right text-fg">{value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

/**
 * The machine's disk, told as one story.
 *
 * This tab is where the disk-pressure alert sends people, so it answers the
 * alert's question first: how full is the machine, and which side of the three
 * thresholds is it on. 85% mails staff once and then weekly; 90% stops
 * placement putting new tenants here; 95% mails staff once more, on its own
 * clock. All three are drawn on the bar rather than remembered.
 */
function MachineDiskCard({
  metrics,
  disk,
}: {
  metrics: ReturnType<typeof useServerMetrics>;
  disk: ReturnType<typeof useServerDiskUsage>;
}) {
  const reading = metrics.data;
  const used = reading?.available ? reading.diskUsedBytes : null;
  const total = reading?.available ? reading.diskTotalBytes : null;
  const fraction = used !== null && total !== null && total > 0 ? used / total : null;

  const dockerBytes = disk.data?.available ? (disk.data.usage?.totalUsageBytes ?? null) : null;
  // What the gauge holds that Docker does not account for: the OS, logs,
  // backup staging, whatever else lives on the root disk. Clamped, because
  // the two figures come from different samplers moments apart.
  const otherBytes =
    used !== null && dockerBytes !== null ? Math.max(0, used - dockerBytes) : null;

  return (
    <Card>
      <CardHeader
        title="The machine"
        description="The whole disk, live from the metrics service — the figure the alerts and placement act on."
      />
      <div className="p-5">
        {metrics.isLoading ? (
          <LoadingBlock />
        ) : !reading?.available ? (
          <Alert tone="info">{reading?.reason ?? "Metrics are not available for this server."}</Alert>
        ) : fraction === null ? (
          <Alert tone="info">The metrics service reported no disk figures.</Alert>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p
                className={cx(
                  "text-3xl font-light tabular-nums tracking-tight",
                  fraction >= SATURATED_ABOVE
                    ? "text-danger"
                    : fraction >= DISK_ALERT_ABOVE
                      ? "text-warn"
                      : "text-fg",
                )}
              >
                {Math.round(fraction * 100)}% full
              </p>
              <p className="text-sm tabular-nums text-fg-subtle">{pair(used, total)}</p>
            </div>

            {/* The two thresholds as ticks on the track itself, so the bar
                answers "how close" without anyone recalling the numbers. */}
            <div className="relative mt-3">
              <div className="h-3 overflow-hidden rounded-full bg-line">
                <div
                  className={cx(
                    "h-full rounded-full transition-[width]",
                    fraction >= SATURATED_ABOVE
                      ? "bg-danger"
                      : fraction >= DISK_ALERT_ABOVE
                        ? "bg-warn"
                        : "bg-mint",
                  )}
                  style={{ width: `${Math.min(100, fraction * 100)}%` }}
                />
              </div>
              {[DISK_ALERT_ABOVE, SATURATED_ABOVE, DISK_CRITICAL_ABOVE].map((mark) => (
                <span
                  key={mark}
                  aria-hidden
                  className="absolute -top-0.5 h-4 w-px bg-fg-subtle/60"
                  style={{ left: `${mark * 100}%` }}
                />
              ))}
            </div>
            <p className="mt-1.5 text-xs text-fg-subtle">
              85% mails staff, then weekly · 90% stops placement sending new tenants here · 95%
              mails staff again
            </p>

            <p
              className={cx(
                "mt-4 text-sm",
                fraction >= SATURATED_ABOVE
                  ? "text-danger"
                  : fraction >= DISK_ALERT_ABOVE
                    ? "text-warn"
                    : "text-fg-muted",
              )}
            >
              {fraction >= DISK_CRITICAL_ABOVE
                ? "Nearly full. Staff have had the second and last alert; a full disk takes every tenant on it down at once — reclaim space or move something now."
                : fraction >= SATURATED_ABOVE
                  ? "Saturated. Placement is refusing this server, and the next alert fires at 95% — reclaim space or move something before it does."
                  : fraction >= DISK_ALERT_ABOVE
                    ? "Filling. Staff were alerted at 85% and hear again weekly; this is the stretch where reclaiming space is still a choice rather than an incident."
                    : "Comfortable. Nothing here needs anyone's attention today."}
            </p>

            <div className="mt-5 grid gap-4 border-t border-line pt-4 sm:grid-cols-3">
              <Stat label="Docker holds" value={formatBytes(dockerBytes)} />
              <Stat label="Everything else" value={formatBytes(otherBytes)} />
              <Stat
                label="Free"
                value={used !== null && total !== null ? formatBytes(total - used) : "—"}
              />
            </div>
            <p className="mt-2 text-xs text-fg-subtle">
              "Everything else" is the OS, logs and whatever lives outside Docker — two samplers
              moments apart, so treat it as a shape, not an audit.
            </p>

            {reading.collectedAt && (
              <p className="mt-3 text-xs text-fg-subtle">
                Sampled {formatRelative(reading.collectedAt)}. Refreshes every 30 seconds.
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/** Mirrors of the API's two alert rungs; the runbook documents all three numbers. */
const DISK_ALERT_ABOVE = 0.85;
const DISK_CRITICAL_ABOVE = 0.95;

/** The order and colour every Docker figure renders in, everywhere on this tab. */
const DOCKER_GROUPS = [
  {
    key: "volumes" as const,
    label: "Volumes",
    swatch: "bg-accent",
    note: "Tenant data — the storage figure tiers state and nothing enforces.",
  },
  { key: "images" as const, label: "Images", swatch: "bg-mint", note: null },
  { key: "containers" as const, label: "Containers", swatch: "bg-fg-subtle", note: null },
  { key: "buildCache" as const, label: "Build cache", swatch: "bg-warn", note: null },
];

/**
 * Where Docker's share actually goes, drawn to proportion.
 *
 * Volumes lead the legend deliberately: on a database server they dominate,
 * they are the one segment cleanup must not casually touch, and they are the
 * unenforced storage story made visible.
 */
function DockerBreakdownCard({ disk }: { disk: ReturnType<typeof useServerDiskUsage> }) {
  const usage = disk.data?.available ? disk.data.usage : null;

  const groups = DOCKER_GROUPS.map((group) => ({
    ...group,
    ...(usage ? usage[group.key] : { count: null, sizeBytes: null }),
  }));
  const known = groups.filter((group) => group.sizeBytes !== null && group.sizeBytes > 0);
  const knownTotal = known.reduce((sum, group) => sum + (group.sizeBytes ?? 0), 0);

  return (
    <Card>
      <CardHeader
        title="Where Docker's share goes"
        description="Read live from the server's settings API."
        action={
          <Button
            variant="secondary"
            size="sm"
            loading={disk.isFetching}
            onClick={() => void disk.refetch()}
          >
            Refresh
          </Button>
        }
      />
      <div className="p-5">
        {disk.isLoading ? (
          <LoadingBlock />
        ) : !usage ? (
          <Alert tone="info">
            {disk.data?.reason ?? "Disk usage is not available for this server."}
          </Alert>
        ) : (
          <>
            {knownTotal > 0 && (
              <div className="flex h-3 overflow-hidden rounded-full">
                {known.map((group) => (
                  <div
                    key={group.key}
                    className={group.swatch}
                    title={`${group.label}: ${formatBytes(group.sizeBytes)}`}
                    style={{ width: `${((group.sizeBytes ?? 0) / knownTotal) * 100}%` }}
                  />
                ))}
              </div>
            )}

            <ul className="mt-4 divide-y divide-line">
              {groups.map((group) => (
                <li key={group.key} className="py-2.5">
                  {/* The numbers wrap as a unit rather than squeezing the
                      label: on a phone the note was being crushed into a
                      one-word-per-line sliver between four fixed columns. */}
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span
                      aria-hidden
                      className={cx("size-2.5 shrink-0 self-center rounded-sm", group.swatch)}
                    />
                    <p className="text-sm text-fg">{group.label}</p>
                    <span className="ml-auto flex items-baseline gap-4">
                      <span className="text-sm tabular-nums text-fg-subtle">
                        {group.count === null ? "—" : formatNumber(group.count)} items
                      </span>
                      <span className="w-20 text-right text-sm font-medium tabular-nums">
                        {formatBytes(group.sizeBytes)}
                      </span>
                      <span className="w-10 text-right text-sm tabular-nums text-fg-subtle">
                        {group.sizeBytes !== null && knownTotal > 0
                          ? `${Math.round(((group.sizeBytes ?? 0) / knownTotal) * 100)}%`
                          : "—"}
                      </span>
                    </span>
                  </div>
                  {group.note && (
                    <p className="mt-0.5 pl-[22px] text-xs text-fg-subtle">{group.note}</p>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-4 grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
              <Stat label="Reclaimable by cleanup" value={formatBytes(usage.reclaimableBytes)} />
              <Stat label="Shared image layers" value={formatBytes(usage.layersSizeBytes)} />
            </div>
            <p className="mt-2 text-xs text-fg-subtle">
              Reclaimable counts what nothing references any more; the cleanup card below is how it
              comes back. Shared layers are counted once however many images use them.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Cleanup, split by what it can cost — verified against Dokploy's source, not
 * guessed from the names.
 *
 * Every action except volumes is volume-safe: system prune runs without the
 * volumes flag, and upstream's own cleanAll excludes volumes deliberately,
 * with a comment calling the stopped-container case dangerous. Their cost is
 * re-pulled images on the next deploy, and a rebuild for every stopped app
 * whose built image they take — slower, never data.
 *
 * Volumes is the one that can take tenant data: it runs docker volume prune
 * with --all, so any volume no container references right now — including a
 * stopped database's data volume — is removed. The split makes that one look
 * different before it is armed, not after.
 */
function CleanupCard({ serverId, canWrite }: { serverId: string; canWrite: boolean }) {
  const cleanup = useRunCleanup(serverId);

  if (!canWrite) {
    return (
      <Card>
        <CardHeader title="Cleanup" />
        <p className="px-5 pb-5 text-sm text-fg-subtle">Docker cleanup requires superadmin access.</p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Cleanup"
        description="Each button arms on the first click and runs on the second."
      />
      <div className="space-y-5 p-5">
        {cleanup.error && <Alert>{cleanup.error.message}</Alert>}
        {cleanup.isSuccess && (
          <Alert tone="info">Cleanup ran. The figures above refresh themselves.</Alert>
        )}

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-fg-subtle">
            Safe for tenant data — none of these touch volumes
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <CleanupButton action="images" label="Remove unused images" cleanup={cleanup} />
            <CleanupButton action="stopped" label="Remove stopped containers" cleanup={cleanup} />
            <CleanupButton action="builder" label="Clear build cache" cleanup={cleanup} />
            <CleanupButton action="prune" label="Prune everything unused" cleanup={cleanup} />
            <CleanupButton action="all" label="Full cleanup" cleanup={cleanup} />
          </div>
          <p className="mt-2 text-xs leading-5 text-fg-subtle">
            The aggressive ones remove every unused image, so the next deploy re-pulls its base
            layers — and that includes the built image of every stopped app on this server, which
            exists nowhere else. Its next start rebuilds it from the commit it last built instead,
            which takes a build. Never data: even "Full cleanup" skips volumes; the deployment
            engine excludes them on purpose.
          </p>
        </div>

        <div className="rounded-lg border border-danger/40 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-danger">
            Destructive — deletes volumes nothing currently mounts
          </p>
          <p className="mt-1 text-xs leading-5 text-fg-subtle">
            The one action that removes tenant data. "Unused" means no container references the
            volume right now — a stopped database's data volume qualifies. Its dumps in backup
            storage survive; the volume itself does not. No undo.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <CleanupButton action="volumes" label="Remove unused volumes" danger cleanup={cleanup} />
          </div>
        </div>
      </div>
    </Card>
  );
}

function DiagnosticsSnapshot({ data }: { data: ServerDiagnostics }) {
  const update = !data.update.available
    ? "Unavailable"
    : data.update.updateAvailable === true
      ? "Update available"
      : data.update.updateAvailable === false
        ? "No update found"
        : "Not reported";

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DiagnosticReading
          label="Platform version"
          value={data.version.available ? data.version.value ?? "Not reported" : "Unavailable"}
          note={data.version.reason}
        />
        <DiagnosticReading
          label="Release tag"
          value={data.releaseTag.available ? data.releaseTag.value ?? "Not reported" : "Unavailable"}
          note={data.releaseTag.reason}
        />
        <DiagnosticReading
          label="Deployment model"
          value={
            data.cloud.available
              ? data.cloud.value === null
                ? "Not reported"
                : data.cloud.value
                  ? "Hosted cloud"
                  : "Self-hosted"
              : "Unavailable"
          }
          note={data.cloud.reason}
        />
        <DiagnosticReading
          label="Update check"
          value={update}
          note={data.update.reason ?? (data.update.latestVersion ? `Latest: ${data.update.latestVersion}` : undefined)}
        />
      </div>

      <div className="mt-6 grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-3">
        <DiagnosticHealth label="Database" check={data.health} />
        <DiagnosticHealth
          label="Postgres"
          available={data.infrastructure.available}
          status={data.infrastructure.postgres}
          reason={data.infrastructure.reason}
        />
        <DiagnosticHealth
          label="Traefik"
          available={data.infrastructure.available}
          status={data.infrastructure.traefik}
          reason={data.infrastructure.reason}
        />
      </div>

      {data.actions.restartTraefik?.outcome === "pending" && !data.actions.restartTraefik.requiresRecovery && (
        <div className="mt-4">
          <Alert tone="info">
            A Traefik restart request is still being dispatched. It cannot be retried or recovered
            until the engine reports a result.
          </Alert>
        </div>
      )}

      {data.actions.restartTraefik?.requiresRecovery && (
        <div className="mt-4">
          <Alert tone="error">
            The last Traefik restart has an unresolved outcome. A superadmin must verify ingress and
            acknowledge recovery before another restart can be requested.
          </Alert>
        </div>
      )}

      {data.actions.updateDokploy?.outcome === "pending" && (
        <div className="mt-4">
          <Alert tone="info">
            An update to{" "}
            {data.actions.updateDokploy.targetVersion ?? "a newer version"} is running on this
            server. It is draining until Maxlayer sees that version reported, which is checked
            every few minutes.
          </Alert>
        </div>
      )}

      {data.actions.updateDokploy?.requiresRecovery && (
        <div className="mt-4">
          <Alert tone="error">
            The last update never reported{" "}
            {data.actions.updateDokploy.targetVersion ?? "the version it should have installed"},
            and this server is still draining. Check the machine, then acknowledge the attempt to
            return it to service.
          </Alert>
        </div>
      )}

      <p className="mt-4 text-xs text-fg-subtle">Collected {formatRelative(data.collectedAt)}.</p>
    </>
  );
}

function DiagnosticReading({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-fg-subtle">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
      {note && <p className="mt-1 text-xs text-fg-subtle">{note}</p>}
    </div>
  );
}

function DiagnosticHealth({
  label,
  check,
  available,
  status,
  reason,
}: {
  label: string;
  check?: DokployDiagnosticCheck;
  available?: boolean;
  status?: DokployDiagnosticStatus;
  reason?: string;
}) {
  const isAvailable = check?.available ?? available ?? false;
  const value = check?.status ?? status ?? "unknown";
  const text = !isAvailable ? "Unavailable" : value === "healthy" ? "Healthy" : value === "unhealthy" ? "Unhealthy" : "Not reported";

  return (
    <div className="bg-surface px-5 py-4">
      <p className="text-xs uppercase tracking-wide text-fg-subtle">{label}</p>
      <p
        className={cx(
          "mt-1 text-lg font-semibold",
          value === "healthy" && isAvailable && "text-mint",
          value === "unhealthy" && isAvailable && "text-danger",
          (!isAvailable || value === "unknown") && "text-fg-subtle",
        )}
      >
        {text}
      </p>
      {(check?.reason ?? reason) && <p className="mt-1 text-xs text-fg-subtle">{check?.reason ?? reason}</p>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-fg-subtle">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

/**
 * A cleanup action button. First click arms it ("Confirm?"), the second runs
 * it; four seconds of inactivity disarms it again.
 */
function CleanupButton({
  action,
  label,
  danger,
  cleanup,
}: {
  action: CleanupAction;
  label: string;
  danger?: boolean;
  cleanup: ReturnType<typeof useRunCleanup>;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);

  return (
    <Button
      variant={armed || danger ? "danger" : "secondary"}
      size="sm"
      loading={cleanup.isPending}
      disabled={cleanup.isPending}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        cleanup.mutate(action, { onSuccess: () => void cleanup.reset() });
      }}
    >
      {armed ? `Confirm ${label.toLowerCase()}?` : label}
    </Button>
  );
}

/**
 * Whole-machine CPU, memory and disk, read from the metrics service.
 *
 * Operator-only by design: this is the aggregate of every tenant sharing the
 * box, so it answers "is this server in trouble" rather than "is this customer
 * using too much". Per-container figures belong to the tenants, not here.
 *
 * Requires the monitoring credentials to be set in the settings card below.
 */
function MetricsCard({
  serverId,
  monitoringConfigured,
}: {
  serverId: string;
  monitoringConfigured: boolean;
}) {
  const metrics = useServerMetrics(serverId);

  return (
    <Card>
      <CardHeader
        title="Machine load"
        description="Asked live. The Load tile above is the cached sample placement ranked on."
      />
      <div className="p-5">
        {metrics.isLoading ? (
          <LoadingBlock />
        ) : !metrics.data?.available ? (
          <Alert tone="info">
            {metrics.data?.reason ?? "Metrics are not available for this server."}
            {!monitoringConfigured &&
              " Monitoring configures itself on the next health sweep; Settings can run it now."}
          </Alert>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-3">
              <Gauge label="CPU" percent={metrics.data.cpuPercent} />
              <Gauge
                label="Memory"
                percent={ratio(metrics.data.memoryUsedBytes, metrics.data.memoryTotalBytes)}
                detail={pair(metrics.data.memoryUsedBytes, metrics.data.memoryTotalBytes)}
              />
              <Gauge
                label="Disk"
                percent={ratio(metrics.data.diskUsedBytes, metrics.data.diskTotalBytes)}
                detail={pair(metrics.data.diskUsedBytes, metrics.data.diskTotalBytes)}
              />
            </div>
            {metrics.data.collectedAt && (
              <p className="mt-4 text-xs text-fg-subtle">
                Sampled {formatRelative(metrics.data.collectedAt)}.
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * A bar, or an em dash.
 *
 * A missing metric renders as "—" rather than an empty bar: a bar at zero reads
 * as idle, which is a different and more dangerous claim than "unknown".
 */
function Gauge({
  label,
  percent,
  detail,
}: {
  label: string;
  percent: number | null;
  detail?: string | null;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-fg-subtle">{label}</p>
        <p className="text-sm font-semibold tabular-nums">
          {percent === null ? "—" : `${Math.round(percent)}%`}
        </p>
      </div>
      {/* `line` rather than `raised`: this is a track a bar sits in, and it has
          to read as a groove against the card behind it. */}
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-line">
        {percent !== null && (
          <div
            className={cx(
              "h-full rounded-full transition-[width]",
              percent >= 90
                ? "bg-danger"
                : percent >= 75
                  ? "bg-warn"
                  : "bg-mint",
            )}
            style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
          />
        )}
      </div>
      {detail && <p className="mt-1 text-xs text-fg-subtle">{detail}</p>}
    </div>
  );
}

function ratio(used: number | null, total: number | null): number | null {
  if (used === null || total === null || total <= 0) return null;
  return (used / total) * 100;
}

function pair(used: number | null, total: number | null): string | null {
  if (used === null || total === null) return null;
  return `${formatBytes(used)} of ${formatBytes(total)}`;
}

function GlanceTile({
  label,
  note,
  children,
}: {
  label: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-surface px-5 py-4">
      <p className="text-xs uppercase tracking-wide text-fg-subtle">{label}</p>
      <div className="mt-1 text-fg">{children}</div>
      {/* Held open either way, so four tiles keep one baseline. */}
      <p className="mt-0.5 min-h-4 text-xs text-fg-subtle">{note ?? ""}</p>
    </div>
  );
}

/** The tightest resource as a number, at tile scale. */
function LoadReading({ server }: { server: ServerDetail }) {
  const usage = tightestUsage(server.capacity);

  if (usage === null) {
    return <span className="text-2xl font-light tracking-tight text-fg-subtle">—</span>;
  }

  return (
    <span
      className={cx(
        "text-2xl font-light tabular-nums tracking-tight",
        usage >= SATURATED_ABOVE && "text-danger",
      )}
    >
      {Math.round(usage * 100)}%
    </span>
  );
}

function loadNote(server: ServerDetail): string | undefined {
  if (tightestUsage(server.capacity) === null) {
    return server.monitoringConfigured ? "not sampled yet" : "no monitoring";
  }
  return `tightest resource · ${formatRelative(server.capacity.collectedAt)}`;
}

/** The region's friendly name and group, looked up in the served catalogue. */
function RegionName({ code }: { code: string | null }) {
  const lookup = useRegionLookup();
  if (!code) return <>—</>;

  const region = lookup.get(code);
  // Falls back to the raw code rather than blanking: an unrecognised region
  // means the console is older than the API, and the code is still useful.
  if (!region) return <Mono>{code}</Mono>;

  return (
    <>
      {region.label}
      <p className="text-xs text-fg-subtle">{region.group}</p>
    </>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-fg-subtle">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function ProjectList({ serverId }: { serverId: string }) {
  const { data, isPending, error } = useServerProjects(serverId, true);

  if (isPending) return <LoadingBlock label="Asking the engine…" />;
  if (error)
    return (
      <div className="p-5">
        <Alert>{error.message}</Alert>
      </div>
    );
  if (data.length === 0) return <EmptyState title="No projects on this server" />;

  return (
    <Table
      head={
        <tr>
          <Th>Project</Th>
          <Th>Maxlayer organization</Th>
          <Th priority="sm">Created</Th>
        </tr>
      }
    >
      {data.map((project) => (
        <tr key={project.projectId}>
          <Td>
            {project.name}
            <Mono className="ml-2 text-fg-subtle">{project.projectId.slice(0, 8)}</Mono>
          </Td>
          <Td>
            {project.organization ? (
              <Link
                to={`/organizations/${project.organization.id}`}
                className="text-accent hover:underline"
              >
                {project.organization.name}
              </Link>
            ) : (
              <span className="text-warn">Unmanaged</span>
            )}
          </Td>
          <Td className="text-fg-subtle" priority="sm">{formatDateTime(project.createdAt)}</Td>
        </tr>
      ))}
    </Table>
  );
}

function SettingsCard({
  serverId,
  initial,
}: {
  serverId: string;
  initial: {
    name: string;
    region: string | null;
    coordinates: { latitude: number; longitude: number } | null;
    maxOrgs: number | null;
    state: ServerState;
    pool: ServerPool;
    notes: string | null;
    originHost: string | null;
    egress: { address: string | null; source: "stated" | "observed" | "none" };
    metricsUrl: string | null;
  };
}) {
  const update = useUpdateServer(serverId);
  const { data: regions } = useRegions();
  const [name, setName] = useState(initial.name);
  const [region, setRegion] = useState(initial.region ?? "");
  const [latitude, setLatitude] = useState(initial.coordinates?.latitude.toString() ?? "");
  const [longitude, setLongitude] = useState(initial.coordinates?.longitude.toString() ?? "");
  const [maxOrgs, setMaxOrgs] = useState(initial.maxOrgs?.toString() ?? "");
  const [state, setState] = useState<ServerState>(initial.state);
  const [pool, setPool] = useState<ServerPool>(initial.pool);
  const [notes, setNotes] = useState(initial.notes ?? "");
  const [originHost, setOriginHost] = useState(initial.originHost ?? "");
  const [egressHost, setEgressHost] = useState(
    initial.egress.source === "stated" ? (initial.egress.address ?? "") : "",
  );
  const [coordinateError, setCoordinateError] = useState<string | null>(null);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setCoordinateError(null);

    const lat = latitude.trim();
    const lon = longitude.trim();
    if (!!lat !== !!lon) {
      setCoordinateError("Latitude and longitude must be entered together.");
      return;
    }

    update.mutate(
      {
        name: name.trim(),
        region: region.trim() || null,
        // Both blank clears the pair, which takes the server out of distance
        // ranking rather than leaving a stale position behind.
        latitude: lat ? Number(lat) : null,
        longitude: lon ? Number(lon) : null,
        maxOrgs: maxOrgs ? Number(maxOrgs) : null,
        state,
        pool,
        notes: notes.trim() || null,
        // Null rather than "" when cleared: absent means "resolve the endpoint".
        originHost: originHost.trim() || null,
        egressHost: egressHost.trim() || null,
      },
    );
  }

  return (
    <Card>
      <CardHeader title="Settings" />
      <form onSubmit={onSubmit} className="space-y-4 px-5 py-4">
        {update.error && <Alert>{update.error.message}</Alert>}
        {update.isSuccess && <Alert tone="success">Saved.</Alert>}

        <Field label="Name" htmlFor="s-name">
          <Input id="s-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field
          label="Placement state"
          htmlFor="s-state"
          hint="Draining stops new tenants and lets empty ones move away."
        >
          <Select
            id="s-state"
            value={state}
            onChange={(e) => setState(e.target.value as ServerState)}
          >
            <option value="active">Active</option>
            <option value="draining">Draining</option>
            <option value="disabled">Disabled</option>
          </Select>
        </Field>

        <Field
          label="Pool"
          htmlFor="s-pool"
          hint="Dedicated takes it out of automatic placement for everybody."
        >
          <Select id="s-pool" value={pool} onChange={(e) => setPool(e.target.value as ServerPool)}>
            <option value="shared">Shared</option>
            <option value="dedicated">Dedicated</option>
          </Select>
        </Field>

        <Field
          label="Region"
          htmlFor="s-region"
          hint={
            "Where the machine is. Tenants land on the nearest one; customers pick its group " +
            "and get the nearest inside it."
          }
        >
          <Select id="s-region" value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="">Not set</option>
            {(regions ?? []).map((entry) => (
              <optgroup key={entry.group} label={entry.group}>
                {entry.regions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Latitude" htmlFor="s-lat" hint="Optional. Overrides the region's point.">
            <Input
              id="s-lat"
              type="number"
              step="any"
              min={-90}
              max={90}
              value={latitude}
              onChange={(e) => {
                setLatitude(e.target.value);
                setCoordinateError(null);
              }}
            />
          </Field>

          <Field label="Longitude" htmlFor="s-lon" hint="Decimal degrees.">
            <Input
              id="s-lon"
              type="number"
              step="any"
              min={-180}
              max={180}
              value={longitude}
              onChange={(e) => {
                setLongitude(e.target.value);
                setCoordinateError(null);
              }}
            />
          </Field>
        </div>

        <MonitoringSetup serverId={serverId} metricsUrl={initial.metricsUrl} />

        {coordinateError && <Alert tone="error">{coordinateError}</Alert>}

        {/*
          Editable after registration, because this is exactly the field somebody
          discovers they need — a server registered while its endpoint was
          unproxied, then put behind the proxy later, silently stops being able to
          name anything until this is set — the server's wildcard records in the
          platform zone point here, and every default domain on it resolves
          through them.
        */}
        <Field
          label="Direct address"
          htmlFor="s-origin"
          hint="Required if the endpoint is proxied. Every address on this server — default domains, custom domains, published databases — resolves here."
        >
          <Input
            id="s-origin"
            placeholder="203.0.113.7"
            autoComplete="off"
            spellCheck={false}
            value={originHost}
            onChange={(e) => setOriginHost(e.target.value)}
          />
        </Field>

        {/*
          The opposite direction to the field above, and a separate one for the
          reason the columns are separate: on a single-homed box both are the
          same address, and the day they are not, a shared field publishes the
          wrong one to customers as the address to allow through their firewall.
          Blank is the ordinary answer — the agent's own poll measures it.
        */}
        <Field
          label="Outbound address"
          htmlFor="s-egress"
          hint="What tenants here call out from, for customers allowlisting us. Blank uses what the firewall agent observes."
        >
          <Input
            id="s-egress"
            placeholder="203.0.113.7"
            autoComplete="off"
            spellCheck={false}
            value={egressHost}
            onChange={(e) => setEgressHost(e.target.value)}
          />
        </Field>

        <Field label="Max organizations" htmlFor="s-max" hint="Blank for no limit.">
          <Input
            id="s-max"
            type="number"
            min={1}
            value={maxOrgs}
            onChange={(e) => setMaxOrgs(e.target.value)}
          />
        </Field>

        <Field label="Notes" htmlFor="s-notes">
          <Textarea id="s-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        <Button type="submit" loading={update.isPending} className="w-full">
          Save changes
        </Button>
      </form>
    </Card>
  );
}

/**
 * The platform's backup storage for this server.
 *
 * One S3-compatible bucket (Cloudflare R2 in practice) per server, entered
 * here. Every database on the server that a customer enables backups for dumps
 * into it on this schedule; customers only toggle, and pay 30% more than their
 * database plan while the toggle is on. The bucket is tested before it is
 * saved — a destination that fails silently produces no backups and no error
 * until somebody needs one that was never written.
 */
export function BackupStorageCard({ serverId }: { serverId: string }) {
  const storage = useServerBackupStorage(serverId);
  const save = useSaveServerBackupStorage(serverId);
  const remove = useRemoveServerBackupStorage(serverId);
  const [endpoint, setEndpoint] = useState("");
  const [bucket, setBucket] = useState("");
  const [region, setRegion] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [frequency, setFrequency] = useState<"every-6-hours" | "daily" | "weekly">("daily");
  const [keepLatest, setKeepLatest] = useState("30");

  const configured = storage.data?.data ?? null;

  useEffect(() => {
    if (!configured) return;
    setEndpoint(configured.endpoint);
    setBucket(configured.bucket);
    setRegion(configured.region);
    setAccessKey(configured.accessKey);
    setFrequency(configured.frequency);
    setKeepLatest(String(configured.keepLatest));
    // The secret never comes back; a blank field means "keep the stored one".
  }, [configured?.serverId]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate({
      endpoint: endpoint.trim(),
      bucket: bucket.trim(),
      region: region.trim(),
      accessKey: accessKey.trim(),
      secretAccessKey: secretAccessKey.trim() || undefined,
      frequency,
      keepLatest: Number(keepLatest) || 30,
    });
  }

  if (storage.isLoading) return <LoadingBlock />;

  return (
    <Card>
      <CardHeader
        title="Backup storage"
        description="The platform's bucket every database on this server backs up into."
      />
      <form onSubmit={onSubmit} className="space-y-4 px-5 py-4">
        {save.error && <Alert>{save.error.message}</Alert>}
        {remove.error && <Alert>{remove.error.message}</Alert>}
        {save.isSuccess && (
          <Alert tone="success">
            {configured?.destinationProvisioned
              ? "Saved. Backups on this server now write to the new bucket."
              : "Saved. The destination is provisioned the first time a tenant turns backups on."}
          </Alert>
        )}

        {configured && (
          <p className="text-xs text-fg-subtle">
            Key ending <Mono>{configured.accessKeyHint || "(short)"}</Mono> ·{" "}
            {!configured.destinationProvisioned ? (
              <span className="text-warn">destination not provisioned yet</span>
            ) : configured.destinationCurrent ? (
              "destination provisioned"
            ) : (
              // Written before the 64 MiB part size: the sweep rewrites it within
              // minutes, so this staying put means the engine is refusing.
              <span className="text-warn">
                destination predates the large-dump settings — dumps over 48 GiB fail until the sweep rewrites it
              </span>
            )}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Endpoint" htmlFor="bs-endpoint" hint="The S3-compatible endpoint, e.g. an R2 URL.">
            <Input
              id="bs-endpoint"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              placeholder="https://abc123.r2.cloudflarestorage.com"
            />
          </Field>

          <Field label="Region" htmlFor="bs-region">
            <Input id="bs-region" value={region} onChange={(e) => setRegion(e.target.value)} />
          </Field>

          <Field label="Bucket" htmlFor="bs-bucket">
            <Input id="bs-bucket" value={bucket} onChange={(e) => setBucket(e.target.value)} />
          </Field>

          <Field label="Access key ID" htmlFor="bs-access">
            <Input id="bs-access" value={accessKey} onChange={(e) => setAccessKey(e.target.value)} />
          </Field>
        </div>

        <Field
          label="Secret access key"
          htmlFor="bs-secret"
          hint={configured ? "Blank keeps the stored key." : "Never stored in plaintext."}
        >
          <Input
            id="bs-secret"
            type="password"
            value={secretAccessKey}
            onChange={(e) => setSecretAccessKey(e.target.value)}
            placeholder={configured ? "•••••••• (unchanged)" : ""}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Backup frequency"
            htmlFor="bs-frequency"
            hint="Inherited by every database with backups enabled on this server."
          >
            <Select
              id="bs-frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as typeof frequency)}
            >
              <option value="every-6-hours">Every 6 hours</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly, on Sunday</option>
            </Select>
          </Field>

          <Field
            label="Keep latest"
            htmlFor="bs-keep"
            hint="How many dumps the engine keeps per database before pruning."
          >
            <Input
              id="bs-keep"
              type="number"
              min={1}
              max={365}
              value={keepLatest}
              onChange={(e) => setKeepLatest(e.target.value)}
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" loading={save.isPending}>
            {configured ? "Save backup storage" : "Configure backup storage"}
          </Button>
          {configured && (
            <Button
              type="button"
              variant="ghost"
              loading={remove.isPending}
              onClick={() => void remove.mutateAsync()}
            >
              Remove
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}

/**
 * Monitoring has no form, because it is not a decision.
 *
 * Dokploy ships its own collector. The platform configures it and finds the
 * address it answers on, at registration and again on any health sweep that
 * finds a healthy server without it — so this reports state rather than
 * collecting it. The button is only a "now, not in five minutes".
 *
 * There were two fields here once, a URL and a token. They are what let this
 * fleet read one collector while configuring another, since nothing checked
 * that the address an operator pasted was the collector Maxlayer manages.
 * Pointing at a separate collector is still possible over the API, and is now
 * a deliberate act rather than the default path.
 */
function MonitoringSetup({ serverId, metricsUrl }: { serverId: string; metricsUrl: string | null }) {
  const provision = useProvisionMonitoring(serverId);
  const endpoint = provision.data?.monitoring.metricsUrl ?? metricsUrl;

  return (
    <div className="rounded-md border border-line bg-surface-subtle p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-fg">Monitoring</p>
          <p className="mt-1 text-xs leading-5 text-fg-subtle">
            {endpoint ? (
              <>
                Configured automatically at <code className="text-fg">{endpoint}</code>. The engine
                reads it, not the API, so it never needs to be publicly reachable.
              </>
            ) : (
              "Not configured yet. The next health sweep sets this up on its own; use Run setup to do it now."
            )}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          loading={provision.isPending}
          onClick={() => provision.mutate()}
        >
          {endpoint ? "Re-run setup" : "Run setup"}
        </Button>
      </div>

      {provision.error && (
        <div className="mt-3">
          <Alert tone="error">{provision.error.message}</Alert>
        </div>
      )}

      {provision.isSuccess && (
        <div className="mt-3">
          <Alert tone={provision.data.monitoring.sampling ? "success" : "info"}>
            {provision.data.monitoring.sampling
              ? `Monitoring is live at ${provision.data.monitoring.metricsUrl}.`
              : `Configured at ${provision.data.monitoring.metricsUrl}. The collector samples on an interval, so readings appear shortly.`}
          </Alert>
        </div>
      )}
    </div>
  );
}

/**
 * The endpoint and the credential together, because they only mean anything as
 * a pair: a key is valid for one instance, so moving a server to a new address
 * usually arrives with a new key. Either may be left alone, and whichever is
 * sent is probed against the instance before it replaces what is stored.
 */
/**
 * The endpoint and the credential, edited where every other setting is edited.
 *
 * This was a dialog opened from the detail header, which put the one form that
 * changes how the platform reaches a machine somewhere other than the page called
 * Settings. It also meant a failed verification — the interesting outcome, since
 * the key is checked against the instance before anything is stored — arrived in
 * a box the operator was trying to dismiss.
 */
function ConnectionCard({ serverId, currentUrl }: { serverId: string; currentUrl: string }) {
  const update = useUpdateServer(serverId);
  const [url, setUrl] = useState(currentUrl);
  const [apiKey, setApiKey] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const nextUrl = url.trim();
    const nextKey = apiKey.trim();

    if (!nextUrl) {
      setFormError("Endpoint is required.");
      return;
    }

    const urlChanged = nextUrl !== currentUrl;
    if (!urlChanged && !nextKey) {
      setFormError("Change the endpoint or enter a new API key.");
      return;
    }

    update.mutate(
      {
        ...(urlChanged ? { url: nextUrl } : {}),
        ...(nextKey ? { apiKey: nextKey } : {}),
      },
      /* The key field is cleared rather than kept: it is write-only, so leaving
         the typed value on screen after a save is the one place it could be read
         back from. */
      { onSuccess: () => setApiKey("") },
    );
  }

  return (
    <Card>
      <CardHeader title="Connection" description="Verified against the server before it is stored." />
      <form onSubmit={onSubmit} className="space-y-4 px-5 py-4">
        {update.error && <Alert>{update.error.message}</Alert>}
        {update.isSuccess && <Alert tone="success">Saved.</Alert>}

        <Field
          label="Endpoint"
          htmlFor="conn-url"
          hint="The engine's base URL, e.g. https://engine.example.com."
        >
          <Input
            id="conn-url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setFormError(null);
            }}
          />
        </Field>

        <Field label="New API key" htmlFor="conn-key" hint="Leave blank to keep the current key.">
          <Input
            id="conn-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              setFormError(null);
            }}
          />
        </Field>

        {formError && <Alert tone="error">{formError}</Alert>}

        <div className="flex justify-end">
          <Button type="submit" loading={update.isPending}>
            Save connection
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function DangerZone({ serverId, name }: { serverId: string; name: string }) {
  const remove = useDeleteServer();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");

  return (
    <Card className="border-danger/30">
      <CardHeader
        title="Deregister server"
        description="Refused while tenants are placed here. Removes our record, not the engine instance."
      />
      <div className="space-y-3 px-5 py-4">
        {remove.error && <Alert>{remove.error.message}</Alert>}
        <Field
          label={
            <>
              Type <TypedPhrase>{name}</TypedPhrase> to confirm
            </>
          }
          htmlFor="confirm-server"
        >
          <Input id="confirm-server" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <Button
          variant="danger"
          disabled={confirm !== name}
          loading={remove.isPending}
          onClick={() =>
            remove.mutate(serverId, { onSuccess: () => void navigate("/servers") })
          }
        >
          Deregister
        </Button>
      </div>
    </Card>
  );
}
